import { put, takeEvery, all, call } from "redux-saga/effects";
import {
  addSymptom,
  createVitals,
  updateForm,
  updateSymptom
} from "../../services/database";
import { throwError } from "../../services/error";
import { uploadFile } from "../../services/storage";
import { documentSchema } from "../../utils/models";
import {
  isValidArray,
  isValidProfileId,
  isValidObject
} from "../../utils/validators";
import { setErrorStatus, setSuccessStatus } from "../status/actions";
import store from "../store/store";
import { retryDataTransfer, uploadDocument } from "../../services/api";
import { lockerSetup, validDocumentType } from "../../utils/constants";
import { getPatientsCareContexts } from "../patients/actions";
import { MAX_PDF_TOTAL_BYTES, mergePdfs } from "../../utils/pdf";

export const documentsActionTypes = {
  ADD_DOCUMENTS: "ADD_DOCUMENTS",
  ADD_SYMPTOM: "ADD_SYMPTOM",
  UPDATE_SYMPTOM: "UPDATE_SYMPTOM",
  UPLOAD_DOCUMENT: "UPLOAD_DOCUMENT",
  ADD_CURRENT_DOCUMENT: "ADD_CURRENT_DOCUMENT",
  REMOVE_CURRENT_DOCUMENT: "REMOVE_CURRENT_DOCUMENT",
  ADD_DOCUMENT_FILTER: "ADD_DOCUMENT_FILTER",
  ADD_DATA_TRANSFER: "ADD_DATA_TRANSFER",
  REMOVE_DOCUMENT_FILTER: "REMOVE_DOCUMENT_FILTER",
  CREATE_VITALS: "CREATE_VITALS",
  UPDATE_FORM: "UPDATE_FORM",
  RETRY_DATA_TRANSFER: "RETRY_DATA_TRANSFER"
};

//set documents listener
function* setDocumentsWorker(action) {
  try {
    const { documents } = action.payload;

    yield put({
      type: "SET_DOCUMENTS_DATA",
      payload: {
        documentsData: documents
      }
    });
  } catch (error) {
    setErrorStatus(error);
  }
}

//set dataTransfer listener
function* setDataTransfersWorker(action) {
  try {
    const { dataTransfer } = action.payload;
    //Documents data for all profiles
    if (Array.isArray(dataTransfer)) {
      //Sort documents data with corresponding profileId's
      let dataTransfers = {};

      dataTransfer.forEach((data) => {
        dataTransfers = { ...dataTransfers, [data.documentId]: data };
      });

      yield put({
        type: "SET_DATA_TRANSFER_DATA",
        payload: {
          dataTransfersData: dataTransfers
        }
      });
    }
  } catch (error) {
    setErrorStatus(error);
  }
}

//upload document worker
function* uploadDocumentWorker(action) {
  try {
    yield setDocumentLoading(true);

    const { auth, patients } = store.getState();
    const { accessToken, currentProfile } = patients;

    const documentData = action.payload.data;
    const { file, documentName, date, time, fileConversion } = documentData;
    const documentType = validDocumentType(documentData.documentType);
    const issueDate = `${date} ${time}`;

    if (!file) {
      throw throwError("custom", "No file provided");
    }

    const formData = new FormData();
    formData.append("issueDate", issueDate);
    formData.append("documentType", documentType);
    formData.append("documentName", documentName);
    formData.append("fileConversion", fileConversion);

    if (file.type === "image") {
      if (!isValidArray(file.src)) throw throwError("custom", "Invalid File");
      file.src.forEach((img) => formData.append("files", img));
    } else if (file.type === "pdf") {
      if (!isValidArray(file.src)) throw throwError("custom", "Invalid File");
      const bytes = yield call(mergePdfs, file.src);
      if (bytes.byteLength > MAX_PDF_TOTAL_BYTES) {
        throw throwError("custom", "Please upload files below 5MB in total");
      }
      formData.append(
        "files",
        new File([bytes], `${documentName}.pdf`, { type: "application/pdf" })
      );
    } else {
      formData.append("files", file);
    }

    const response = yield uploadDocument(
      auth.data.accessToken,
      accessToken?.[currentProfile]?.token,
      currentProfile,
      formData
    );

    if (response?.success) {
      setSuccessStatus("Document uploaded successfully");
      getPatientsCareContexts();
      documentData.navigate("/patientTimeline", { replace: true });
    } else if (response?.error?.name === "requestError") {
      setErrorStatus({ code: "custom", message: response.error.message });
    } else if (response?.error) {
      throw response.error;
    }

    yield setDocumentLoading(false);
  } catch (error) {
    setErrorStatus(error);
    yield setDocumentLoading(false);
  }
}

//add symptom worker
function* addSymptomWorker(action) {
  try {
    yield setDocumentLoading(true);

    const { auth, patients } = store.getState();
    const { profiles, currentProfile } = patients;
    const profileData = profiles?.[currentProfile];

    if (!isValidProfileId(currentProfile) || !isValidObject(profileData)) {
      throwError("custom", "Current profile is empty");
    }

    const symptomData = { ...action.payload.data };
    const authData = auth.data;

    // Handle audio upload
    if (
      isValidObject(symptomData.url) &&
      typeof symptomData.url.audio === "object"
    ) {
      const metaData = {
        uploaderId: currentProfile,
        uploaderUserName: profileData.name,
        uploaderUserType: "patient"
      };
      const response = yield uploadFile(
        profileData.phoneNumber,
        currentProfile,
        "symptoms",
        [symptomData.url.audio],
        metaData
      );
      symptomData.url.audio = response
        ? `gs://${response.metadata.bucket}/${response.metadata.fullPath}`
        : undefined;
      if (!response) delete symptomData.url.audio;
    }

    // Handle images upload
    if (
      isValidObject(symptomData.url) &&
      isValidArray(symptomData.url.images)
    ) {
      const metaData = {
        uploaderId: currentProfile,
        uploaderUserName: profileData.name,
        uploaderUserType: "patient"
      };
      const imagesUrl = [];
      for (const image of symptomData.url.images) {
        const response = yield uploadFile(
          profileData.phoneNumber,
          currentProfile,
          "symptoms",
          [image],
          metaData
        );
        if (response)
          imagesUrl.push(
            `gs://${response.metadata.bucket}/${response.metadata.fullPath}`
          );
      }
      symptomData.url.images = imagesUrl;
    }

    symptomData.phoneNumber = profileData.phoneNumber;
    symptomData.fullName = authData.displayName.patient;

    const schemaData = documentSchema(symptomData, "create", "symptom");
    const addSymptomResponse = yield addSymptom(schemaData);

    yield put({ type: "SET_DOCUMENTS_LOADING", payload: { loading: false } });

    if (
      typeof addSymptomResponse === "string" &&
      isValidObject(action.payload.navigate)
    ) {
      action.payload.navigate(-1, { replace: true });
      setSuccessStatus("Symptom added successfully");
    }
  } catch (error) {
    yield setDocumentLoading(false);
    setErrorStatus(error);
  }
}

//update symptom worker
function* updateSymptomWorker(action) {
  yield setDocumentLoading(true);
  try {
    const { auth, patients, documents } = store.getState();

    const { profiles, currentProfile } = patients;
    const { name, phoneNumber } = auth.data;
    const currentDocument = documents.currentDocument;
    const profileData = profiles?.[currentProfile];

    if (!isValidObject(currentDocument) || !isValidObject(profileData)) {
      return;
    }

    const symptomData = { ...action.payload.data };
    symptomData.phoneNumber = phoneNumber;
    symptomData.fullName = name;

    // Handle audio deletion
    // if (symptomData.audio.length === 0) {
    //   yield deleteFile(currentDocument.audio);
    //   delete symptomData.audio;
    // }
    // // Handle new audio upload
    // else if (symptomData.audio !== currentDocument.audio) {
    //   if (!!symptomData.audio && typeof symptomData.audio === "object") {
    //     const metaData = {
    //       uploaderId: currentProfile,
    //       uploaderUserName: profileData.public.fullName,
    //       uploaderUserType: "patient"
    //     };
    //     const response = yield uploadFile(
    //       profileData.phoneNumber,
    //       currentProfile,
    //       "symptoms",
    //       symptomData.audio,
    //       metaData
    //     );
    //     if (response) {
    //       symptomData.audio = `gs://${response.metadata.bucket}/${response.metadata.fullPath}`;
    //     } else {
    //       delete symptomData.audio;
    //     }
    //   }
    // }

    const modelResponseData = documentSchema(symptomData, "update", "symptom");
    const res = yield updateSymptom(symptomData.documentId, modelResponseData);

    if (typeof res === "string" && isValidObject(action.payload.navigate)) {
      action.payload.navigate(-1, { replace: true });
      setSuccessStatus("Symptom updated successfully");
    }
    yield setDocumentLoading(false);
  } catch (error) {
    yield setDocumentLoading(false);

    setErrorStatus(error);
  }
}
//update symptom worker
function* setCurrentDocumentWorker(action) {
  yield setDocumentLoading(true);
  try {
    const { documents } = store.getState();

    const documentsData = documents.data?.flat();

    if (!isValidArray(documentsData)) {
      return;
    }

    const currentDocument = documentsData.find(
      (doc) => doc.documentId === action.payload.documentId
    );

    if (currentDocument) {
      yield put({
        type: "SET_CURRENT_DOCUMENT",
        payload: { currentDocument }
      });
    }
    yield setDocumentLoading(false);
  } catch (error) {
    yield setDocumentLoading(false);

    setErrorStatus(error);
  }
}

function* removeCurrentDocumentWorker() {
  try {
    yield setDocumentLoading(true);
    yield put({
      type: "SET_CURRENT_DOCUMENT",
      payload: {
        currentDocument: {}
      }
    });
    yield setDocumentLoading(false);
  } catch (error) {
    yield setDocumentLoading(false);
    setErrorStatus(error);
  }
}

function* addDocumentFilterWorker(action) {
  try {
    const {
      documents: { filters }
    } = store.getState();

    const { type, filterValue } = action.payload;

    switch (type) {
      case "careContextDocumentId": {
        yield put({
          type: "FILTER_BY_CARE_CONTEXT",
          payload: { careContext: filterValue }
        });
        return;
      }

      case "document": {
        let documentsType = [...filters.documentType];

        if (filterValue === "allDocuments") {
          documentsType = [];
        } else if (documentsType.includes(filterValue)) {
          documentsType = documentsType.filter((item) => item !== filterValue);
        } else {
          documentsType = [...documentsType, filterValue];
        }

        yield put({
          type: "FILTER_BY_DOCUMENTS",
          payload: { documentsType }
        });
        return;
      }

      case "doctor": {
        let doctorsName = [...filters.doctors];

        doctorsName = doctorsName.includes(filterValue)
          ? doctorsName.filter((item) => item !== filterValue)
          : [...doctorsName, filterValue];

        yield put({
          type: "FILTER_BY_DOCTORS",
          payload: { doctorsName }
        });
        return;
      }

      case "clinic": {
        let clinicsName = [...filters.clinics];

        clinicsName = clinicsName.includes(filterValue)
          ? clinicsName.filter((item) => item !== filterValue)
          : [...clinicsName, filterValue];

        yield put({
          type: "FILTER_BY_CLINICS",
          payload: { clinicsName }
        });
        return;
      }

      default:
        return;
    }
  } catch (error) {
    setErrorStatus(error);
  }
}
function* removeDocumentFilterWorker(action) {
  if (action.payload.clear !== "doctorAndClinic") {
    return;
  }

  yield put({
    type: "FILTER_BY_DOCTORS",
    payload: {
      doctorsName: []
    }
  });
  yield put({
    type: "FILTER_BY_CLINICS",
    payload: {
      clinicsName: []
    }
  });
  yield put({
    type: "FILTER_BY_CARE_CONTEXT",
    payload: {
      careContext: []
    }
  });
  return;
}

function* createVitalsWorker(action) {
  try {
    yield setDocumentLoading(true);

    const { auth, profile } = store.getState();
    const { vitalsData, navigate } = action.payload;

    const currentProfile = profile.currentProfile;
    const { displayName, phoneNumber } = auth.data;

    vitalsData.from = {
      name: displayName.patient,
      type: "patient",
      id: currentProfile
    };

    vitalsData.to = {
      fullName: displayName.patient,
      phoneNumber,
      id: currentProfile
    };

    vitalsData.phoneNumber = phoneNumber;

    const vitalsSchemaResponse = documentSchema(vitalsData, "create", "vitals");
    const vitalsResponse = yield createVitals(vitalsSchemaResponse);

    if (typeof vitalsResponse === "string") {
      if (isValidObject(navigate)) {
        navigate(-1, { replace: true });
      }
      setSuccessStatus("Vitals added successfully");
    } else {
      throwError("custom", "Something went wrong, please try again later");
    }
    yield setDocumentLoading(false);
  } catch (error) {
    yield setDocumentLoading(false);
    setErrorStatus(error);
  }
}

function* updateFormWorker(action) {
  try {
    yield setDocumentLoading(true);
    //update clinic form for patient admission
    const { documentId, data, navigate } = action.payload;

    const response = yield updateForm(documentId, data);
    if (response === true) {
      navigate("/", { replace: true });
      yield put({
        type: "SET_DOCUMENTS_LOADING",
        payload: {
          loading: false
        }
      });
    }
  } catch (error) {
    yield setDocumentLoading(false);
    setErrorStatus(error);
  }
}

export function* retryDataTransferWorker(action) {
  try {
    yield setDocumentLoading(true);
    const { app, auth, patients } = store.getState();

    if (!app.featureFlags?.healthData) {
      throwError(
        "custom",
        "Under Maintenance! Please check back after sometimes"
      );
    }

    const { subscription, currentProfile, accessToken } = patients;

    const profileSubscriptions = subscription?.[currentProfile];

    const subscriptionData = isValidObject(profileSubscriptions)
      ? Object.values(profileSubscriptions)[0]
      : null;

    const { careContexts, healthId, hipId } = action.payload || {};

    const response = yield retryDataTransfer(
      auth.data.accessToken,
      accessToken?.[currentProfile]?.token,
      {
        clinicId: subscriptionData?.subscription?.hiu?.id || lockerSetup.locker,
        ...(subscriptionData?.documentId
          ? { subscriptionId: subscriptionData.documentId }
          : {}),
        patientId: currentProfile,
        ...(healthId ? { healthId } : {}),
        ...(hipId ? { hipId } : {}),
        ...(careContexts ? { careContexts } : {})
      }
    );
    const { data, success } = response;

    if (success === false) {
      setErrorStatus({
        code: "custom",
        message: data?.message
      });
    }

    if (success === true) {
      setSuccessStatus(data.message);
    }
  } catch (error) {
    setErrorStatus(error);
  } finally {
    yield setDocumentLoading(false);
  }
}

export default function* documentsWatcher() {
  yield all([
    takeEvery("ADD_DOCUMENTS", setDocumentsWorker),
    takeEvery("ADD_DATA_TRANSFER", setDataTransfersWorker),
    takeEvery("UPDATE_FORM", updateFormWorker),
    takeEvery("ADD_CURRENT_DOCUMENT", setCurrentDocumentWorker),
    takeEvery("REMOVE_CURRENT_DOCUMENT", removeCurrentDocumentWorker),
    takeEvery("ADD_SYMPTOM", addSymptomWorker),
    takeEvery("CREATE_VITALS", createVitalsWorker),
    takeEvery("UPDATE_SYMPTOM", updateSymptomWorker),
    takeEvery("UPLOAD_DOCUMENT", uploadDocumentWorker),
    takeEvery("ADD_DOCUMENT_FILTER", addDocumentFilterWorker),
    takeEvery("REMOVE_DOCUMENT_FILTER", removeDocumentFilterWorker),
    takeEvery("RETRY_DATA_TRANSFER", retryDataTransferWorker)
  ]);
}

function* setDocumentLoading(bool) {
  yield put({
    type: "SET_DOCUMENTS_LOADING",
    payload: {
      loading: bool
    }
  });
}
